import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Rocket, ExternalLink, Copy, CheckCircle2, Loader2, DollarSign, CalendarDays } from "lucide-react";

export default function HypedditBriefDialog({ release, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(release?.hypeddit_brief || null);
  const [dailyBudget, setDailyBudget] = useState(
    release?.hypeddit_brief?.daily_budget || 7
  );
  const [totalDays, setTotalDays] = useState(
    release?.hypeddit_brief?.total_days || 7
  );
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!release) return;
    if (!release.spotify_url) {
      toast.error("Add a Spotify URL to this release first (under Settings on the card).");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(`/releases/${release.release_id}/hypeddit-brief`, {
        daily_budget: Number(dailyBudget),
        total_days: Number(totalDays),
      });
      setBrief(r.data);
      toast.success("Brief ready — copy-paste into Hypeddit below.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Brief generation failed";
      toast.error(typeof detail === "string" ? detail : "Brief generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copyChecklist = () => {
    if (!brief?.checklist_text) return;
    navigator.clipboard.writeText(brief.checklist_text).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2500); },
      () => toast.error("Copy failed"),
    );
  };

  if (!release) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0A0A0A] border-white/10 rounded-sm w-[calc(100vw-1rem)] max-w-3xl text-white max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        data-testid="hypeddit-brief-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-tight flex items-center gap-3">
            <Rocket className="text-[#FF3B00]" size={24} />
            Hypeddit campaign · {release.title}
          </DialogTitle>
        </DialogHeader>

        <p className="text-white/55 text-sm leading-relaxed mb-6">
          Alchemist Lab drafts the full campaign config (genre, similar artists, copy, budget).
          Click <span className="text-white">Open Hypeddit</span> to launch the new-campaign wizard
          with your Spotify URL already queued. Paste the checklist into the remaining fields —
          30 seconds tops.
        </p>

        {/* Budget + length controls */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="label-mono text-white/45 flex items-center gap-2 mb-1.5">
              <DollarSign size={11} /> Daily budget (USD)
            </label>
            <input
              className="mp-input"
              type="number"
              step="0.5" min="1" max="100"
              value={dailyBudget}
              onChange={(e) => setDailyBudget(e.target.value)}
              data-testid="hypeddit-budget"
            />
          </div>
          <div>
            <label className="label-mono text-white/45 flex items-center gap-2 mb-1.5">
              <CalendarDays size={11} /> Campaign length (days)
            </label>
            <input
              className="mp-input"
              type="number"
              step="1" min="3" max="30"
              value={totalDays}
              onChange={(e) => setTotalDays(e.target.value)}
              data-testid="hypeddit-days"
            />
          </div>
        </div>

        <button
          className="btn-amber w-full mb-6"
          onClick={generate}
          disabled={loading}
          data-testid="hypeddit-generate-btn"
        >
          {loading ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : (
            <><Rocket size={14} /> {brief ? "Regenerate brief" : "Generate brief"}</>
          )}
        </button>

        {brief && (
          <div className="space-y-5 fade-in">
            {/* Summary row */}
            <div className="grid grid-cols-3 gap-3 p-4 border border-white/10 bg-white/[0.02]">
              <div>
                <div className="label-mono text-white/40">Genre</div>
                <div className="text-white font-medium mt-1" data-testid="brief-genre">{brief.genre}</div>
              </div>
              <div>
                <div className="label-mono text-white/40">Budget</div>
                <div className="text-white font-medium mt-1">
                  ${brief.daily_budget}/day · {brief.total_days}d
                </div>
                <div className="label-mono text-white/45 mt-0.5">~${brief.total_spend} total</div>
              </div>
              <div>
                <div className="label-mono text-white/40">Audience</div>
                <div className="text-white/80 text-sm mt-1 leading-snug">{brief.audience_vibe}</div>
              </div>
            </div>

            {/* Launch row — moved up so the user can open Hypeddit in a side tab first */}
            <div className="flex flex-col md:flex-row gap-3">
              <a
                href={brief.hypeddit_new_campaign_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-amber flex-1"
                data-testid="hypeddit-open-link"
              >
                <ExternalLink size={14} /> Open Hypeddit (Spotify URL queued)
              </a>
              <button
                className="btn-ghost-line flex-1"
                onClick={copyChecklist}
                data-testid="hypeddit-copy-checklist"
              >
                {copied ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy full checklist</>}
              </button>
            </div>

            {/* 10-step walkthrough */}
            <div className="space-y-3">
              <div className="label-mono text-white/45">Hypeddit wizard — paste each step</div>
              {brief.steps?.map((s) => (
                <details
                  key={s.n}
                  className="border border-white/[0.08] bg-white/[0.02] group"
                  open={s.n <= 2}
                >
                  <summary className="cursor-pointer px-4 py-3 flex items-center gap-3 hover:bg-white/[0.04]">
                    <span className="w-6 h-6 rounded-full bg-[#FF3B00]/20 border border-[#FF3B00]/50 flex items-center justify-center text-[11px] text-[#FF3B00] font-mono shrink-0">
                      {s.n}
                    </span>
                    <span className="font-medium text-white flex-1">{s.name}</span>
                    <Copy
                      size={12}
                      className="text-white/40 hover:text-white cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        navigator.clipboard.writeText(s.paste).then(() => toast(`Step ${s.n} copied`));
                      }}
                    />
                  </summary>
                  <div className="px-4 pb-4 space-y-2">
                    <pre className="text-[12px] text-white/85 whitespace-pre-wrap font-mono leading-relaxed bg-black/40 p-3 rounded-sm">
                      {s.paste}
                    </pre>
                    <div className="text-xs text-white/45 italic leading-relaxed">{s.note}</div>
                  </div>
                </details>
              ))}
            </div>

            {/* Copy (pitch + description) */}
            <div>
              <div className="label-mono text-white/45 mb-1">One-line pitch</div>
              <div className="p-3 border border-white/10 bg-white/[0.02] text-white/90 text-sm" data-testid="brief-pitch">
                {brief.pitch}
              </div>
            </div>
            <div>
              <div className="label-mono text-white/45 mb-1">Description</div>
              <div className="p-3 border border-white/10 bg-white/[0.02] text-white/90 text-sm leading-relaxed whitespace-pre-wrap" data-testid="brief-description">
                {brief.description}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
